import React from 'react';
import { Check } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const DEFAULT_STEPS = [
  'Preparing Document',
  'Translating Content',
  'Reviewing Translation',
  'Translation Ready',
];

const TranslationProgress = ({ step, steps }) => {
  const STEPS = steps || DEFAULT_STEPS;
  const { isDarkMode } = useTheme();

  if (!step || step < 1) return null;

  const pendingCircleBg = isDarkMode ? '#111827' : '#fff';
  const pendingBorder = isDarkMode ? '#374151' : '#e5e7eb';
  const pendingLineColor = isDarkMode ? '#374151' : '#e5e7eb';

  return (
    <div className="w-full px-4 py-5" data-testid="translation-progress">
      <div className="flex items-center justify-between relative">
        {STEPS.map((label, i) => {
          const stepNum = i + 1;
          const isCompleted = step > stepNum;
          const isCurrent = step === stepNum;
          const isPending = step < stepNum;

          return (
            <React.Fragment key={stepNum}>
              {/* Connecting line before this step (skip first) */}
              {i > 0 && (
                <div
                  className="flex-1 h-0.5 mx-1"
                  style={{
                    backgroundColor: isCompleted || isCurrent ? '#2563eb' : pendingLineColor,
                    transition: 'background-color 0.4s ease',
                  }}
                />
              )}

              <div className="flex flex-col items-center" style={{ minWidth: 0 }}>
                {/* Circle */}
                <div style={{ position: 'relative' }}>
                  {isCurrent && (
                    <span
                      style={{
                        position: 'absolute',
                        inset: -4,
                        borderRadius: '50%',
                        background: 'rgba(37,99,235,0.2)',
                        animation: 'tp-pulse 1.5s ease-in-out infinite',
                      }}
                    />
                  )}
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 13,
                      flexShrink: 0,
                      transition: 'all 0.3s ease',
                      ...(isCompleted
                        ? { background: '#2563eb', border: 'none', color: '#fff' }
                        : isCurrent
                        ? { background: pendingCircleBg, border: '2px solid #2563eb', color: '#2563eb' }
                        : { background: pendingCircleBg, border: `2px solid ${pendingBorder}`, color: '#9ca3af' }),
                    }}
                  >
                    {isCompleted ? (
                      <Check size={15} strokeWidth={2.5} />
                    ) : (
                      <span>{stepNum}</span>
                    )}
                  </div>
                </div>

                {/* Label */}
                <span
                  style={{
                    marginTop: 8,
                    fontSize: 11,
                    fontWeight: isCurrent ? 600 : 400,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    color: isCompleted
                      ? '#2563eb'
                      : isCurrent
                      ? '#1d4ed8'
                      : '#9ca3af',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <style>{`
        @keyframes tp-pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.2; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
};

export default TranslationProgress;
